import * as React from 'react';

import { Close } from '@mint-lib/icons';
import { cn } from '@mint-lib/tw-utils';
import { type VariantProps } from 'class-variance-authority';
import { Dialog as DialogPrimitive } from 'radix-ui';

import { Button } from './button.jsx';
import { typographyVariants } from './typography.jsx';
import { dialogVariants } from './variants.jsx';

function Dialog({
  ...props
}: Readonly<React.ComponentProps<typeof DialogPrimitive.Root>>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  ...props
}: Readonly<React.ComponentProps<typeof DialogPrimitive.Portal>>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 z-overlay bg-ui-overlay data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  );
}

function DialogRoot({
  className,
  children,

  size = 'm',

  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> &
  VariantProps<typeof dialogVariants>) {
  return (
    <DialogPrimitive.Content
      data-slot="dialog-content"
      data-size={size}
      className={cn(
        dialogVariants({ size }),

        className,
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(typographyVariants({ variant: 'bodyLong01' }), className)}
      {...props}
    />
  );
}

function DialogHeader({
  className,
  showCloseButton = true,
  onClose,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean;
  onClose?: () => void;
}) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        'flex h-14 items-center p-4 group-data-[size=full]/dialog:h-16 group-data-[size=full]/dialog:pl-10',
        className,
      )}
      {...props}
    >
      {props.children}
      {showCloseButton && (
        <DialogPrimitive.Close
          asChild
          data-slot="dialog-close"
          className={cn(
            'absolute top-5 right-5 group-data-[size=full]/dialog:top-6.5 group-data-[size=full]/dialog:left-4',
          )}
        >
          <Button variant="plain" icon onClick={onClose}>
            <Close className="fill-tertiary-main" />
            <span className="sr-only">Close</span>
          </Button>
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'flex h-18 w-full items-center justify-end gap-2 p-4',
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(typographyVariants({ variant: 'h3' }), className)}
      {...props}
    />
  );
}

function DialogContent({
  className,
  ...props
}: Readonly<{
  children: React.ReactNode;
  className?: string;
}>) {
  return (
    <div
      className={cn(
        'flex h-full min-h-0 w-full overflow-y-auto p-4',
        className,
      )}
    >
      {props.children}
    </div>
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogRoot,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
